import type { LegalSection } from '../constants/legalDocuments.constants'
import {
  ABOUT_SECTIONS,
  SUPPORT_SECTIONS,
  VIRTUAL_CURRENCY_SECTION,
} from '../constants/legalDocuments.constants'
import type { LegalDocumentVariant } from '../types/legalDocument.types'
import type { Translations } from '../../i18n/translations'
import type { Language } from '../../i18n/translations'
import { getLocalizedLegalSections } from '../constants/legalDocumentsLocalized.constants'

export interface LegalDocumentPageContent {
  title: string
  sections: readonly LegalSection[]
}

/**
 * Guarantees the Virtual Currency section is present in the Terms for every language.
 *
 * Localized documents are whole hand-written arrays per language, so a new English section
 * is missing from all of them until someone translates it. Rather than publish Terms that
 * omit the coin disclosure in five languages, we append the English section. When a real
 * translation lands it will carry the same `key`, and this becomes a no-op for that language.
 */
function withVirtualCurrencySection(
  sections: readonly LegalSection[]
): readonly LegalSection[] {
  const alreadyPresent = sections.some(
    (section) => section.key === VIRTUAL_CURRENCY_SECTION.key
  )
  return alreadyPresent ? sections : [...sections, VIRTUAL_CURRENCY_SECTION]
}

/**
 * Resolves title and section copy for the document screen.
 *
 * About and Support are English-only company pages — they reuse the document chrome but
 * are not part of the localized legal set.
 */
export function getLegalDocumentPageContent(
  variant: LegalDocumentVariant,
  t: Translations,
  language: Language
): LegalDocumentPageContent {
  if (variant === 'about') {
    return { title: t.legal.aboutDocumentTitle, sections: ABOUT_SECTIONS }
  }
  if (variant === 'support') {
    return { title: t.legal.supportDocumentTitle, sections: SUPPORT_SECTIONS }
  }

  const localizedSections = getLocalizedLegalSections(language)
  if (variant === 'privacy') {
    return {
      title: t.legal.privacyDocumentTitle,
      sections: localizedSections.privacy,
    }
  }
  if (variant === 'dataDeletion') {
    return {
      title: t.legal.dataDeletionDocumentTitle,
      sections: localizedSections.dataDeletion,
    }
  }
  if (variant === 'terms') {
    return {
      title: t.legal.termsDocumentTitle,
      sections: withVirtualCurrencySection(localizedSections.terms),
    }
  }
  return {
    title: t.legal.creditsDocumentTitle,
    sections: localizedSections.credits,
  }
}

/** Legal documents carry a version line; company pages and credits do not. */
export function shouldShowDocumentVersion(variant: LegalDocumentVariant): boolean {
  return variant === 'terms' || variant === 'privacy' || variant === 'dataDeletion'
}
